import { DISCOUNT_TYPES, calculateLineTotals, calculateQuoteTotals } from "./quoteCalculations";
import { createIssuerSnapshot, getCompanyById } from "./mockCompanies";
import { getProductById } from "./mockProducts";

const createLocalId = (prefix = "id") =>
	`${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const createEmptyClient = () => ({
	rut: "",
	nombre: "",
	correo: "",
	telefono: "",
	giro: "",
	domicilio: "",
	comuna: "",
});

export const createEmptyItem = (productId = "") => {
	const product = productId ? getProductById(productId) : null;
	const base = {
		id: createLocalId("item"),
		productId: product?.id || "",
		nombre: product?.nombre || "",
		descripcion: product?.descripcion || "",
		cantidad: 1,
		precioUnitario: product?.precioNeto || 0,
		tipoDescuento: DISCOUNT_TYPES.NONE,
		valorDescuento: 0,
	};
	const totals = calculateLineTotals(base);
	return { ...base, ...totals };
};

export const createEmptyQuote = ({ seller, company } = {}) => {
	const issuer = company || null;
	const now = new Date().toISOString();

	return {
		id: createLocalId("quote"),
		number: null,
		status: "borrador",

		issuerCompanyId: issuer?.id || null,
		issuerSnapshot: issuer ? createIssuerSnapshot(issuer) : null,

		sellerId: seller?.id || "",
		sellerSnapshot: {
			nombre: seller?.nombre || "",
			correo: seller?.correo || "",
			telefono: seller?.telefono || "",
			rol: seller?.rol || "",
		},

		client: createEmptyClient(),
		items: [],

		globalDiscount: {
			type: DISCOUNT_TYPES.NONE,
			value: 0,
			amount: 0,
		},

		subtotal: 0,
		lineDiscountsTotal: 0,
		netTotal: 0,
		taxRate: issuer?.tipoTributario === "afecta" ? issuer.tasaIva || 19 : 0,
		taxTotal: 0,
		grandTotal: 0,

		paymentMethod: "",
		customPaymentMethod: "",
		validityDays: 15,
		customValidityDays: "",
		executionTime: "Entre 3 y 5 días hábiles desde la recepción de los antecedentes.",
		conditions:
			"La prestación del servicio comenzará una vez confirmado el pago y recibidos todos los antecedentes necesarios.",
		notes: "",

		createdAt: now,
		updatedAt: now,
		issuedAt: null,
	};
};

/**
 * Genera numeración local simulada.
 * TODO futuro: reemplazar por generación atómica en PostgreSQL
 * (secuencia / función transaccional) vía Supabase o backend.
 */
export const generateMockQuoteNumber = (company, existingQuotes = []) => {
	const year = new Date().getFullYear();
	const prefix = company?.prefijoNumero || company?.issuerSnapshot?.prefijoNumero || "DN";
	const pattern = `COT-${prefix}-${year}-`;

	const related = existingQuotes
		.map((q) => q.number)
		.filter((number) => typeof number === "string" && number.startsWith(pattern));

	const maxSeq = related.reduce((max, number) => {
		const seq = Number(number.split("-").pop());
		return Number.isFinite(seq) && seq > max ? seq : max;
	}, 0);

	const next = String(maxSeq + 1).padStart(4, "0");
	return `${pattern}${next}`;
};

export const applyTotalsToQuote = (quote) => {
	const company =
		quote.issuerSnapshot ||
		(quote.issuerCompanyId ? getCompanyById(quote.issuerCompanyId) : null);

	const tipoTributario = company?.tipoTributario || "afecta";
	const tasaIva = company?.tasaIva ?? (tipoTributario === "afecta" ? 19 : 0);

	const items = (quote.items || []).map((item) => {
		const line = calculateLineTotals(item);
		return { ...item, ...line };
	});

	const totals = calculateQuoteTotals({
		items,
		globalDiscount: quote.globalDiscount,
		tipoTributario,
		tasaIva,
	});

	return {
		...quote,
		items,
		issuerSnapshot: company
			? {
					...createIssuerSnapshot(company),
					...(quote.issuerSnapshot || {}),
					tipoTributario,
					tasaIva,
			  }
			: quote.issuerSnapshot,
		globalDiscount: {
			...quote.globalDiscount,
			amount: totals.globalDiscountAmount,
		},
		subtotal: totals.subtotal,
		lineDiscountsTotal: totals.lineDiscountsTotal,
		netTotal: totals.netTotal,
		taxRate: totals.taxRate,
		taxTotal: totals.taxTotal,
		grandTotal: totals.grandTotal,
		updatedAt: new Date().toISOString(),
	};
};

export const canEmitQuote = (quote) => {
	const errors = [];
	if (!quote.issuerCompanyId || !quote.issuerSnapshot) {
		errors.push("Debes seleccionar una empresa emisora.");
	}
	if (!quote.client?.rut?.trim()) errors.push("El RUT del cliente es obligatorio.");
	if (!quote.client?.nombre?.trim()) {
		errors.push("El nombre o razón social del cliente es obligatorio.");
	}
	if (!quote.items?.length) errors.push("Debes agregar al menos un producto o servicio.");
	const invalidPrice = (quote.items || []).some(
		(item) => !item.nombre?.trim() || Number(item.precioUnitario) < 0 || Number(item.cantidad) <= 0
	);
	if (invalidPrice) {
		errors.push("Cada producto debe tener nombre, cantidad válida y precio no negativo.");
	}
	if (!quote.paymentMethod) errors.push("Debes seleccionar una forma de pago.");
	if (quote.paymentMethod === "Otra" && !quote.customPaymentMethod?.trim()) {
		errors.push("Debes indicar la forma de pago personalizada.");
	}
	return { ok: errors.length === 0, errors };
};

export const PAYMENT_METHODS = [
	"Transferencia bancaria",
	"Pago en línea",
	"Pago al contado",
	"50% de anticipo y 50% contra entrega",
	"Pago completo antes de comenzar",
	"Otra",
];

export const VALIDITY_OPTIONS = [
	{ label: "7 días", value: 7 },
	{ label: "10 días", value: 10 },
	{ label: "15 días", value: 15 },
	{ label: "30 días", value: 30 },
	{ label: "Personalizada", value: "custom" },
];

export const STATUS_LABELS = {
	borrador: "Borrador",
	emitida: "Emitida",
	anulada: "Anulada",
};
